import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Breadcrumb from "@/components/layout/Breadcrumb";
import { brand, stats, testimonials } from "@/mocks/site";
import { principles, milestones } from "@/mocks/about";
import { useSeo } from "@/hooks/useSeo";

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于热瓜雷达",
      description: "热瓜雷达的定位、发展理念、内容原则与联系方式。",
      url: "https://w01xfj.cn/about",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: brand.name,
      slogan: brand.slogan,
      email: brand.email,
      url: "https://w01xfj.cn/",
    },
  ];

  useSeo({
    title: "关于热瓜雷达｜定位·理念·内容原则与联系我们",
    description:
      "认识热瓜雷达：专注全网热瓜同类盘点、系列事件与对比分析的内容平台。了解我们的发展理念、可溯源反网暴内容原则，并获取投稿与合作联系邮箱。",
    canonical: "https://w01xfj.cn/about",
    jsonLd,
  });

  return (
    <>
      <Navbar />
      <main>
        <div className="mx-auto max-w-container px-4 pt-10 md:px-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        </div>

        <section className="mx-auto max-w-container px-4 pt-6 md:px-6">
          <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">关于我们</span>
              <h1 className="mt-2 text-3xl text-foreground-950 md:text-4xl">热瓜雷达是谁</h1>
              <p className="mt-4 text-sm leading-7 text-foreground-600">
                {brand.name}是一个专注于<b className="text-foreground-900">全网热瓜同类盘点、系列事件与对比分析</b>的内容平台。
                我们相信热点总在更替，但<b className="text-foreground-900">剧情套路与传播规律是重复的</b>——把同类事件摆在同一张桌上，按时间线串联梳理，让相似剧情和后续走向一次摸清。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                从一张时间线表出发，我们逐步成长为覆盖热门事件追踪、专题盘点与概念对照的全景吃瓜平台，
                目标始终是同一个：<b className="text-primary-600">让信息更清晰，让判断更自由，让吃瓜更冷静</b>。
              </p>
            </div>
            <div className="grid w-full max-w-md grid-cols-2 gap-3 lg:w-auto">
              {stats.map((s) => (
                <div key={s.label} className="flex items-center gap-3 rounded-2xl border border-background-200 bg-background-50 p-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                    <i className={`${s.icon} text-lg`}></i>
                  </span>
                  <div>
                    <p className="font-display text-xl text-foreground-950">{s.value}</p>
                    <p className="text-xs text-foreground-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-container px-4 py-12 md:px-6">
          <h2 className="text-2xl text-foreground-950 md:text-3xl">我们一路走来的脚印</h2>
          <ol className="relative mt-8 border-l-2 border-background-300 pl-6 md:pl-8">
            {milestones.map((m, i) => (
              <li key={m.time} className="relative pb-8 last:pb-0">
                <span className="absolute -left-[37px] top-0 flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50 md:-left-[49px]">
                  <i className={i === 0 ? "ri-flag-line" : i === 1 ? "ri-stack-line" : "ri-rocket-line"}></i>
                </span>
                <span className="font-display text-sm tracking-wide text-secondary-700">{m.time}</span>
                <h3 className="mt-1 text-lg font-semibold text-foreground-950">{m.title}</h3>
                <p className="mt-2 max-w-2xl text-sm leading-7 text-foreground-600">{m.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="bg-background-100/60">
          <div className="mx-auto max-w-container px-4 py-12 md:px-6 md:py-14">
            <h2 className="text-2xl text-foreground-950 md:text-3xl">内容原则，是雷打不动的底线</h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">这四条原则决定了热瓜雷达生产什么、不生产什么。</p>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((p) => (
                <div key={p.title} className="rounded-2xl border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-container px-4 py-12 md:px-6">
          <h2 className="text-2xl text-foreground-950 md:text-3xl">读者怎么说我们</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-6">
                <i className="ri-double-quotes-l text-2xl text-primary-300"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-7 text-foreground-700">{t.quote}</blockquote>
                <figcaption className="mt-5 flex items-center gap-2 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
                    <i className="ri-user-smile-line"></i>
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{t.name}</span>
                    <span className="block text-xs text-foreground-500">{t.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-container px-4 pb-4 md:px-6">
          <div className="rounded-3xl bg-primary-600 p-6 text-background-50 md:p-10">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div className="max-w-xl">
                <h2 className="text-xl text-background-50 md:text-2xl">有选题想分享，或想聊聊合作？</h2>
                <p className="mt-2 text-sm leading-6 text-background-100/90">
                  无论是投稿、转载授权还是品牌合作，都欢迎通过邮箱联系我们。收到后我们会在工作日内回复。
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <a href={`mailto:${brand.email}`} className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 text-sm font-medium text-primary-700 transition hover:bg-background-100">
                  <i className="ri-mail-line"></i>
                  {brand.email}
                </a>
                <Link to="/faq" className="inline-flex h-11 items-center justify-center whitespace-nowrap rounded-full border border-background-50/50 px-6 text-sm text-background-50 transition hover:bg-background-50/10">
                  先看常见问题
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}